import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Badge, ListGroup, Button, Form, Alert, Modal } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import Accordion from "react-bootstrap/Accordion";
import './PersonalizedHotelPackage.css';

interface Props{
  fullName: string;
}


const InteractiveHotelPackageCustomizer = (props: Props) => {
  const [selectedOptions, setSelectedOptions] = useState<any>({
    accessibility: ['wheelchair-access', 'bathroom-fixtures', 'roll-in-shower', 'ground-floor', 'mobility-aids'],
    dietary: ['vegetarian-kitchen', 'diabetic-room-service'],
    wellness: ['massage-therapy', 'spa-access'],
    cultural: ['architecture-tour', 'custom-itinerary'],
    amenities: ['king-bed', 'lake-view'],
    additionalServices: []
  });

  const [guestInfo, setGuestInfo] = useState<any>({
    name: 'Ms. Jane Doe',
    specialNeeds: ['wheelchair', 'vegetarian', 'diabetic'],
    loyaltyLevel: 'Frequent Traveler Plus'
  });

  const [packageTotal, setPackageTotal] = useState(200);
  const [showSummary, setShowSummary] = useState(false);
  const [activeCustomization, setActiveCustomization] = useState(null);

  // Package options with pricing
  const packageOptions : any = {
    accessibility: [
      { id: 'wheelchair-access', name: 'Wheelchair-accessible suite with wide doorways', price: 0, included: true },
      { id: 'bathroom-fixtures', name: 'Accessible bathroom fixtures and controls', price: 0, included: true },
      { id: 'roll-in-shower', name: 'Roll-in shower with grab bars and fold-down seat', price: 0, included: true },
      { id: 'ground-floor', name: 'Ground floor access with elevator backup', price: 0, included: true },
      { id: 'mobility-aids', name: 'Additional mobility aids (shower chair, reacher)', price: 0, included: true },
      { id: 'emergency-system', name: 'Enhanced emergency call system', price: 10, included: false }
    ],
    dietary: [
      { id: 'vegetarian-kitchen', name: 'Certified vegetarian & gluten-free kitchen', price: 0, included: true },
      { id: 'specialty-menus', name: 'Italian & Japanese specialty menus', price: 20, included: false },
      { id: 'insulin-storage', name: 'Insulin storage refrigerator in suite', price: 10, included: false },
      { id: 'diabetic-room-service', name: '24/7 diabetic-friendly room service', price: 0, included: true },
      { id: 'nutrition-consultation', name: 'Personal nutrition consultation', price: 50, included: false },
      { id: 'meal-prep-service', name: 'Daily meal preparation service', price: 75, included: false }
    ],
    wellness: [
      { id: 'massage-therapy', name: 'Complimentary 60-minute massage therapy', price: 0, included: true },
      { id: 'facial-treatment', name: 'Relaxing facial treatment', price: 40, included: false },
      { id: 'spa-access', name: 'Access to tranquil spa facilities', price: 0, included: true },
      { id: 'detox-therapy', name: 'Detox and relaxation therapy options', price: 60, included: false },
      { id: 'personal-trainer', name: 'Personal fitness trainer session', price: 80, included: false },
      { id: 'meditation-class', name: 'Private meditation and mindfulness class', price: 45, included: false }
    ],
    cultural: [
      { id: 'architecture-tour', name: 'Private historical architecture tour (2h)', price: 0, included: true },
      { id: 'museum-access', name: 'Museum and theater venue access', price: 35, included: false },
      { id: 'heritage-experience', name: 'Swiss heritage and cultural themes', price: 50, included: false },
      { id: 'language-guide', name: 'English-speaking guide for small groups', price: 25, included: false },
      { id: 'custom-itinerary', name: 'Personalized cultural itinerary planning', price: 0, included: true },
      { id: 'art-workshop', name: 'Local art and craft workshop experience', price: 65, included: false }
    ],
    amenities: [
      { id: 'king-bed', name: 'King Size Bed with Premium linens & pillows', price: 0, included: true },
      { id: 'lake-view', name: 'Lake View - Panoramic Swiss scenery', price: 0, included: true },
      { id: 'pool-gym', name: 'Pool & Gym Access - Full fitness facilities', price: 15, included: false },
      { id: 'balcony-upgrade', name: 'Private balcony with lake view', price: 15, included: false },
      { id: 'premium-minibar', name: 'Premium minibar with diabetic-friendly options', price: 30, included: false },
      { id: 'room-service-upgrade', name: '24/7 premium room service', price: 40, included: false }
    ],
    additionalServices: [
      { id: 'airport-transfer', name: 'Luxury accessible airport transfer', price: 85, included: false },
      { id: 'personal-concierge', name: 'Dedicated personal concierge service', price: 50, included: false },
      { id: 'medical-support', name: '24/7 medical emergency support', price: 30, included: false },
      { id: 'translation-service', name: 'Professional translation services', price: 50, included: false },
      { id: 'shopping-assistance', name: 'Personal shopping assistance', price: 40, included: false },
      { id: 'custom-dietary-prep', name: 'Custom meal preparation by chef', price: 120, included: false }
    ]
  };

  // Calculate total price based on selected options
  useEffect(() => {
    let total = 200; // Base price
    
    Object.keys(selectedOptions).forEach((category: string) => {
      selectedOptions[category].forEach((optionId: string) => {
        const option = packageOptions[category]?.find((opt: { id: string }) => opt.id === optionId);
        if (option) {
          total += option.price;
        }
      });
    });

    // Apply loyalty discount
    if (guestInfo.loyaltyLevel === 'Frequent Traveler Plus') {
      total *= 0.9; // 10% discount
    }

    setPackageTotal(Math.round(total));
  }, [selectedOptions, guestInfo.loyaltyLevel]);

  const handleOptionToggle = (category: string | number, optionId: any) => {
    setSelectedOptions((prev: { [x: string]: any; }) => ({
      ...prev,
      [category]: prev[category].includes(optionId)
        ? prev[category].filter((id: any) => id !== optionId)
        : [...prev[category], optionId]
    }));
  };

  const getTotalSelectedOptions = () => {
    console.log("Selected Options: ", selectedOptions);
    return Object.values(selectedOptions).reduce((total:any, options:any) => total + options.length, 0) as any;
  };

  const getCategoryTotal = (category: string) => {
    return selectedOptions[category].reduce((total: any, optionId: string) => {
      const option = packageOptions[category]?.find((opt: { id: string }) => opt.id === optionId);
      return total + (option ? option.price : 0);
    }, 0);
  };

  const CategoryCard = (cat : any) => (
    <Card className={`h-100 border-${cat.bgColor} shadow-sm`}>
      <Card.Header className={`bg-${cat.bgColor} ${cat.textColor} d-flex justify-content-between align-items-center`}>
        <div>
          <h6 className="mb-0">
            <i className={`me-2`}></i>
            {cat.title}
          </h6>
        </div>
        <div className="d-flex align-items-center gap-2">
          <Badge >
            {selectedOptions[cat.category].length} selected
          </Badge>
          <Badge >
            +{getCategoryTotal(cat.category)} EUR
          </Badge>
        </div>
      </Card.Header>
      <Card.Body className="p-0">
        <ListGroup variant="flush">
            {packageOptions[cat.category].map((option:any) => (
            <ListGroup.Item key={option.id} className="px-3 py-2">
                <div className="d-flex align-items-center w-100">
                <Form.Check
                    type="checkbox"
                    id={`${cat.category}-${option.id}`}
                    checked={selectedOptions[cat.category].includes(option.id)}
                    onChange={() => handleOptionToggle(cat.category, option.id)}
                    className="me-3"
                />
                <div className="d-flex justify-content-between align-items-center flex-grow-1">
                    <div className="flex-grow-1">
                    <span className="fw-500" style={{ float: 'left' }}>{option.name}</span>
                    </div>
                    <span className={`fw-bold ${option.price === 0 ? 'text-success' : 'text-primary'}`}>
                    {option.price === 0 ? 'Free' : `+${option.price} EUR`}
                    </span>
                </div>
                </div>
            </ListGroup.Item>
            ))}
        </ListGroup>
        </Card.Body>



    </Card>
  );

  return (
    <Container fluid className="py-4 bg-light">
      <Row className="justify-content-center">
        <Col lg={10}>
          {/* Header with Guest Info and Summary */}
          <Card className="shadow-lg border-0 mb-4">
            <Card.Header className="bg-primary text-white p-4">
              <Row className="align-items-center">
                <Col md={6}>
                  <div>
                    <small className="text-light opacity-75 d-block mb-1">
                      Customize Your Experience
                    </small>
                    <h2 className="mb-2 fw-bold">{props.fullName}</h2>
                    <div className="d-flex gap-2 flex-wrap">
                      {guestInfo.specialNeeds.map((need: any | null | undefined) => (
                        <Badge key={need}  className="px-2 py-1">
                          {need}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </Col>
                <Col md={6} className="text-md-end">
                  <div className="text-light">
                    <h3 className="mb-1">{packageTotal} EUR</h3>
                    <small className="opacity-75">per night</small>
                    <div className="mt-2">
                      <Button 
                        variant="light" 
                        size="sm" 
                        onClick={() => setShowSummary(true)}
                        className="me-2"
                      >
                        <i className="fas fa-eye me-2"></i>
                        Preview Package
                      </Button>
                      <Button variant="success" size="sm">
                        <i className="fas fa-check me-2"></i>
                        Confirm Booking
                      </Button>
                    </div>
                  </div>
                </Col>
              </Row>
            </Card.Header>
          </Card>

          {/* Quick Stats */}
          <Row className="mb-4">
            <Col md={3}>
              <Card className="text-center border-0 bg-info text-white">
                <Card.Body className="py-3">
                  <h4 className="mb-1">{getTotalSelectedOptions()}</h4>
                  <small>Selected Options</small>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center border-0 bg-success text-white">
                <Card.Body className="py-3">
                  <h4 className="mb-1">{Math.round((packageTotal - 200) / packageTotal * 100)}%</h4>
                  <small>Customization Level</small>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center border-0 bg-warning text-dark">
                <Card.Body className="py-3">
                  <h4 className="mb-1">{packageTotal - 200} EUR</h4>
                  <small>Added Value</small>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center border-0 bg-danger text-white">
                <Card.Body className="py-3">
                  <h4 className="mb-1">10%</h4>
                  <small>Loyalty Discount</small>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Customization Categories */}
          <Row className="g-4 mb-4">
            <Col lg={6}>
              <CategoryCard
                category="accessibility"
                title="Accessibility Features"
                icon="fas fa-wheelchair"
                bgColor="primary"
                textColor="text-white"
              />
            </Col>
            <Col lg={6}>
              <CategoryCard
                category="dietary"
                title="Dietary & Medical Care"
                icon="fas fa-utensils"
                bgColor="success"
                textColor="text-white"
              />
            </Col>
            <Col lg={6}>
              <CategoryCard
                category="wellness"
                title="Wellness & Spa Services"
                icon="fas fa-spa"
                bgColor="info"
                textColor="text-white"
              />
            </Col>
            <Col lg={6}>
              <CategoryCard
                category="cultural"
                title="Cultural Experiences"
                icon="fas fa-landmark"
                bgColor="warning"
                textColor="text-dark"
              />
            </Col>
            <Col lg={6}>
              <CategoryCard
                category="amenities"
                title="Room Amenities"
                icon="fas fa-bed"
                bgColor="dark"
                textColor="text-white"
              />
            </Col>
            <Col lg={6}>
              <CategoryCard
                category="additionalServices"
                title="Additional Services"
                icon="fas fa-concierge-bell"
                bgColor="secondary"
                textColor="text-white"
              />
            </Col>
          </Row>

          {/* Package Summary */}
          <Card className="shadow border-0">
            <Card.Header className="bg-light">
              <h5 className="mb-0">
                <i className="fas fa-clipboard-list me-2"></i>
                Your Customized Package Summary
              </h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={8}>
                  <div className="mb-3">
                    <h6>Premium Accessible Executive Suite</h6>
                    <p className="text-muted mb-0">All-Inclusive Personalized Experience Package</p>
                  </div>
                  <Accordion className="border-0">
                    {Object.entries(selectedOptions).map((selectedOption: any) => {
                      let category = selectedOption.key;
                      let options = selectedOption.values;
                      options.length > 0 && (
                        <Card >
                            <Card.Header style={{textAlign: "left"}}>
                            <Accordion.Toggle className="d-flex justify-content-between w-100 me-3" eventKey={category} style={{textAlign: "left", cursor: "pointer", border: "none", backgroundColor: "transparent"}}>
                                <div className="d-flex justify-content-between w-100 me-3">
                                    <span className="fw-bold text-capitalize">
                                        {category.replace(/([A-Z])/g, ' $1').trim()}
                                    </span>
                                    <div>
                                        <Badge className="me-2">
                                        {options.length} items
                                        </Badge>
                                        <Badge >
                                        +{getCategoryTotal(category)} EUR
                                        </Badge>
                                    </div>
                                </div>
                                </Accordion.Toggle>
                            </Card.Header>
                        
                        <Accordion.Collapse key={category} eventKey={category.toString()}>
                            <Card.Body style={{textAlign: "left"}}>
                              <ListGroup variant="flush">
                                {options.map((optionId: React.Key | null | undefined) => {
                                    const option = packageOptions[category]?.find((opt: { id: React.Key | null | undefined; }) => opt.id === optionId);
                                    return option ? (
                                    <ListGroup.Item key={optionId} className="px-0 py-1 border-0">
                                        <div className="d-flex justify-content-between">
                                        <small>{option.name}</small>
                                        <small className="text-success fw-bold">
                                            {option.price === 0 ? 'Included' : `+${option.price} EUR`}
                                        </small>
                                        </div>
                                    </ListGroup.Item>
                                    ) : null;
                                })}
                            </ListGroup>
                            </Card.Body>
                          </Accordion.Collapse>
                        </Card>
                      )
            })}
                  </Accordion>
                </Col>
                
                <Col md={4}>
                  <Card className="bg-light border-0">
                    <Card.Body>
                      <h6 className="mb-3">Pricing Breakdown</h6>
                      <div className="d-flex justify-content-between mb-2">
                        <span>Base Package</span>
                        <span>200 EUR</span>
                      </div>
                      <div className="d-flex justify-content-between mb-2">
                        <span>Customizations</span>
                        <span>+{packageTotal - 180} EUR</span>
                      </div>
                      <div className="d-flex justify-content-between mb-2 text-success">
                        <span>Loyalty Discount (10%)</span>
                        <span>-{Math.round((packageTotal / 0.9) - packageTotal)} EUR</span>
                      </div>
                      <hr />
                      <div className="d-flex justify-content-between mb-3">
                        <strong>Total per Night</strong>
                        <strong className="text-primary">{packageTotal} EUR</strong>
                      </div>
                      
                      <Alert variant="info" className="mt-3 mb-0">
                        <small>
                          <i className="fas fa-info-circle me-2"></i>
                          Free cancellation up to 24 hours before check-in
                        </small>
                      </Alert>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Card.Body>
          </Card>

         
        </Col>
      </Row>

      {/* Package Preview Modal */}
      <Modal show={showSummary} onHide={() => setShowSummary(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Package Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center mb-4">
            <h4>Your Customized Experience</h4>
            <p className="text-muted">Total: {packageTotal} EUR per night</p>
          </div>

          {Object.entries(selectedOptions).map(([category, options]) => (
            (Array.isArray(options) && options.length > 0) && (
              <div key={category} className="mb-3">
                <h6 className="text-capitalize mb-2">
                  {category.replace(/([A-Z])/g, ' $1').trim()}
                </h6>
                <div className="row g-2">
                  {options.map((optionId: React.Key | null | undefined) => {
                    const option = packageOptions[category]?.find((opt: { id: any; }) => opt.id === optionId);
                    return option ? (
                      <div key={optionId} className="col-md-6">
                        <div className="p-2 bg-light rounded">
                          <small className="d-block">{option.name}</small>
                          <small className="text-success fw-bold">
                            {option.price === 0 ? 'Included' : `+${option.price} EUR`}
                          </small>
                        </div>
                      </div>
                    ) : null;
                  })}
                </div>
              </div>
            )
          ))}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSummary(false)}>
            Close
          </Button>
          <Button variant="primary">
            Confirm Package
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default InteractiveHotelPackageCustomizer;
